import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { ArrowRight, Check, Target, Calendar, Users } from 'lucide-react';

const HERO_IMAGE =
  'https://static.prod-images.emergentagent.com/jobs/75765fb2-f934-4a78-b9d4-83c5dd8dbc83/images/bd61f053099fd6579cdaf6441ba19135df23f64c767249d73e3823b839ee5528.jpeg';

/* Reveal on scroll */
const Reveal = ({ children, delay = 0, as: Tag = 'div', className = '' }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && (setVisible(true), obs.disconnect()),
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <Tag
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      } ${className}`}
    >
      {children}
    </Tag>
  );
};

const howItWorks = [
  {
    icon: Target,
    tag: '01',
    title: 'Commit to a goal',
    body: 'Set the goal, name the target. Make the quiet promise to yourself that today is the day.',
  },
  {
    icon: Calendar,
    tag: '02',
    title: 'Track the everyday',
    body: 'Check in daily. Watch the streak grow. See the proof fill in on your calendar.',
  },
  {
    icon: Users,
    tag: '03',
    title: 'Share the load',
    body: 'Invite a friend to the same goal. Accountability is quieter when it isn\u2019t alone.',
  },
];

const proPerks = [
  'Unlimited goals',
  'Shared goals with friends',
  'Weekly habit tracker',
  'Goal groups & organization',
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const primaryCta = user ? '/dashboard' : '/auth';
  const primaryLabel = user ? 'Open your dashboard' : 'Start for free';

  return (
    <div className="bg-[#FAF9F5] text-[#1A1A1A]">
      {/* ============================ NAV ============================ */}
      <header className="sticky top-0 z-40 bg-[#FAF9F5]/85 backdrop-blur-md border-b border-[#EEEAE0]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2.5"
            data-testid="landing-logo"
          >
            <div className="w-7 h-7 rounded-md overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_fc70e8a7-91d2-4d7f-977d-e07f378252ba/artifacts/zlmzll26_Monk%20Mode.png"
                alt="Monk Mode"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="font-semibold text-lg tracking-tight">Monk Mode</span>
          </button>
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#5F5F5F]">
            <a href="#how" className="hover:text-[#1A1A1A] transition">How it works</a>
            <a href="#pricing" className="hover:text-[#1A1A1A] transition">Pricing</a>
            <button onClick={() => navigate('/faq')} className="hover:text-[#1A1A1A] transition">FAQ</button>
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/auth')}
              className="hidden sm:inline-flex text-sm text-[#5F5F5F] hover:text-[#1A1A1A] px-4 py-2 transition"
              data-testid="nav-signin"
            >
              Sign in
            </button>
            <button
              onClick={() => navigate(primaryCta)}
              className="sl-pill-dark text-sm px-5 py-2"
              data-testid="nav-primary-cta"
            >
              {primaryLabel} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </header>

      {/* ============================ HERO ============================ */}
      <section className="max-w-6xl mx-auto px-6 pt-16 sm:pt-24 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="sl-section-tag mb-5">FOR WORK, FOR HABITS, FOR WHAT MATTERS.</p>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="text-5xl sm:text-6xl lg:text-[4.5rem] leading-[1.02] tracking-[-0.035em] font-semibold">
                A little more discipline.<br />
                <span className="text-[#9E8778] font-medium">A little less drift.</span>
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-6 max-w-lg text-lg text-[#5F5F5F] leading-relaxed">
                A quiet place to keep the goals you keep meaning to keep. Put something at stake,
                show up every day, let the habit do the heavy lifting.
              </p>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <button
                  onClick={() => navigate(primaryCta)}
                  className="sl-pill-dark"
                  data-testid="hero-primary-cta"
                >
                  {primaryLabel} <ArrowRight className="w-4 h-4" />
                </button>
                <a href="#how" className="sl-pill-ghost" data-testid="hero-secondary-cta">
                  See how it works
                </a>
              </div>
            </Reveal>
            <Reveal delay={320}>
              <p className="mt-5 text-xs tracking-wider uppercase text-[#9E8778] font-mono">
                Free · Pro $4.99/mo · Cancel anytime
              </p>
            </Reveal>
          </div>

          <Reveal delay={200} className="lg:col-span-5">
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden sl-card p-2 shadow-[0_30px_60px_-20px_rgba(26,26,26,0.18)]">
                <img
                  src={HERO_IMAGE}
                  alt="Journal and morning ritual"
                  className="w-full h-auto rounded-2xl object-cover aspect-[4/5] sm:aspect-[5/6]"
                />
              </div>
              <div className="pointer-events-none absolute -z-10 -top-10 -right-10 w-56 h-56 rounded-full bg-[#FDE8DF] blur-3xl opacity-70" />
              <div className="pointer-events-none absolute -z-10 -bottom-10 -left-10 w-56 h-56 rounded-full bg-[#F4F3EE] blur-3xl opacity-80" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============================ HOW IT WORKS ============================ */}
      <section id="how" className="max-w-6xl mx-auto px-6 py-20 sm:py-24">
        <Reveal>
          <p className="sl-section-tag mb-5">HOW IT WORKS</p>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="text-4xl sm:text-5xl leading-[1.05] tracking-[-0.03em] font-semibold max-w-2xl">
            Three small habits.<br />
            <span className="text-[#9E8778] font-medium">One quieter mind.</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {howItWorks.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={i} delay={100 + i * 80}>
                <div className="sl-card p-7 h-full">
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-11 h-11 rounded-xl bg-[#F4F3EE] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#1A1A1A]" strokeWidth={1.6} />
                    </div>
                    <span className="font-mono text-xs tracking-[0.18em] text-[#9E8778]">{s.tag}</span>
                  </div>
                  <h3 className="text-xl font-semibold tracking-tight mb-2">{s.title}</h3>
                  <p className="text-sm text-[#5F5F5F] leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ============================ PRICING ============================ */}
      <section id="pricing" className="max-w-6xl mx-auto px-6 py-20 sm:py-24">
        <Reveal>
          <p className="sl-section-tag mb-5">PRICING</p>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="text-4xl sm:text-5xl leading-[1.05] tracking-[-0.03em] font-semibold max-w-2xl">
            Free to try.<br />
            <span className="text-[#9E8778] font-medium">Honest when you stay.</span>
          </h2>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-2 gap-5">
          <Reveal delay={120}>
            <div className="sl-card p-8 h-full flex flex-col">
              <div className="flex items-baseline justify-between mb-5">
                <h3 className="text-xl font-semibold tracking-tight">Free</h3>
                <span className="text-3xl font-semibold">$0</span>
              </div>
              <p className="text-[#5F5F5F] text-sm leading-relaxed mb-6">
                Everything you need to build a habit. Forever free.
              </p>
              <ul className="space-y-3 flex-1 mb-8">
                {['Unlimited goals', 'Daily & calendar views', 'Streak tracking', 'Monk ranks'].map((p, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm">
                    <Check className="w-4 h-4 mt-0.5 text-[#1A1A1A] flex-shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate(primaryCta)}
                className="sl-pill-ghost w-full"
                data-testid="pricing-free-cta"
              >
                Start free
              </button>
            </div>
          </Reveal>

          <Reveal delay={200}>
            <div className="sl-card p-8 h-full flex flex-col border-[#1A1A1A] relative">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-xl font-semibold tracking-tight">Pro</h3>
                <span className="sl-chip-coral">Recommended</span>
              </div>
              <div className="flex items-baseline justify-between mb-5">
                <div>
                  <span className="text-3xl font-semibold">$4.99</span>
                  <span className="text-sm text-[#9E8778] ml-1">/mo</span>
                </div>
              </div>
              <p className="text-[#5F5F5F] text-sm leading-relaxed mb-6">
                Everything in Free, plus unlimited goals, shared challenges with friends, and the weekly tracker.
              </p>
              <ul className="space-y-3 flex-1 mb-8">
                {proPerks.map((p, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm">
                    <Check className="w-4 h-4 mt-0.5 text-coral flex-shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate(user ? '/upgrade' : '/auth')}
                className="sl-pill-dark w-full"
                data-testid="pricing-pro-cta"
              >
                Go Pro <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============================ CLOSING ============================ */}
      <section className="max-w-3xl mx-auto px-6 py-20 sm:py-28 text-center">
        <Reveal>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.03em] font-semibold">
            One small promise.<br />
            <span className="text-[#9E8778] font-medium">Starting today.</span>
          </h2>
        </Reveal>
        <Reveal delay={120}>
          <p className="mt-5 text-base sm:text-lg text-[#5F5F5F]">
            Put one thing on your list. Give yourself one less thing to break.
          </p>
        </Reveal>
        <Reveal delay={200}>
          <div className="mt-8">
            <button
              onClick={() => navigate(primaryCta)}
              className="sl-pill-dark text-base px-8 py-4"
              data-testid="closing-cta"
            >
              {primaryLabel} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </Reveal>
      </section>

      {/* ============================ FOOTER ============================ */}
      <footer className="border-t border-[#EEEAE0] py-10 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_fc70e8a7-91d2-4d7f-977d-e07f378252ba/artifacts/zlmzll26_Monk%20Mode.png"
                alt="Monk Mode"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="font-semibold text-base tracking-tight">Monk Mode</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-[#9E8778] tracking-wider uppercase font-mono">
            <button onClick={() => navigate('/faq')} className="hover:text-[#1A1A1A] transition">FAQ</button>
            <button onClick={() => navigate('/privacy')} className="hover:text-[#1A1A1A] transition" data-testid="footer-privacy-link">
              Privacy
            </button>
            <button onClick={() => navigate('/terms')} className="hover:text-[#1A1A1A] transition" data-testid="footer-terms-link">
              Terms
            </button>
            <button onClick={() => navigate('/auth')} className="hover:text-[#1A1A1A] transition">Sign in</button>
            <span>© {new Date().getFullYear()} Monk Mode</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
